import * as React from 'react';

import type PostType from '@research-api/utils/api/typings/PostType';

import AdContainer from '@/components/ads/AdContainer';
import AdContainerIds from '@/components/ads/AdContainerIds';
import SectionHeader from '@/components/display/headers/SectionHeader';
import Newsletter from '@/components/pages/frontpage/components/Feature/Right/Newsletter';
import NewsSection from '@/components/pages/frontpage/components/News/NewsSection';
import ArticleContent from '@/components/pages/news-article/components/ArticleContent';
import RecentResearch from '@/components/sections/Right/RecentResearch';
import UpcomingEvent from '@/components/sections/Right/UpcomingEvent';
import subscribeToNewsletter from '@/utils/functions/subscribeToNewsletter';
import type { Event, ArticleType } from '@/types/ArticleType';
import type { Category } from '@/utils/functions/wordpress/fetchCategories';

type NewsArticlePageProps = {
    article: ArticleType;
    readMoreArticles: ArticleType[];
    recentResearch: PostType | null;
    categories: Category[];
    upcomingEvents: Event[];
};

const NewsArticlePage = (props: NewsArticlePageProps) => {
    const { article, recentResearch, readMoreArticles, categories, upcomingEvents } = props;

    return (
        <main className="border-gray-200">
            <section className="flex flex-row flex-wrap xl:flex-nowrap">
                <div className="basis-1/1 lg:basis-4/6 h-full p-5 md:p-8 lg:p-10 border-r-0 lg:border-r border-b lg:border-b-0">
                    <ArticleContent {...article} shareUrl={`${process.env.PUBLIC_URL}/news/${article.slug}`} />
                </div>
                <div className="basis-1/1 lg:basis-2/6">
                    <div>
                        <Newsletter
                            title="Newsletter"
                            subTitle="Blockworks Daily"
                            inputPlaceholder="Email address"
                            buttonText="Subscribe"
                            onButtonClick={subscribeToNewsletter}
                        />
                    </div>
                    <div>
                        {upcomingEvents.map(event => (
                            <UpcomingEvent
                                key={event.slug}
                                className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 border-t"
                                header="upcoming event"
                                title={event.title}
                                description={event.excerpt}
                                location={event.customFields?.location || ''}
                                date={event.customFields?.text_date || ''}
                                learnMoreUrl={`/event/${event.slug}`}
                                registerUrl={
                                    event.customFields.registration_button_link?.url ||
                                    event.customFields.header_button_1_link?.url ||
                                    ''
                                }
                            />
                        ))}
                    </div>
                    <div>
                        <RecentResearch recentResearch={recentResearch} className="pb-8" />
                    </div>
                    <div className="relative lg:sticky top-8 justify-self-start px-12" style={{ alignSelf: 'start' }}>
                        <AdContainer
                            googleAdId={AdContainerIds.NewsArticleRight600x300}
                            className="invisible xl:visible h-0 w-0 xl:h-[600px] xl:w-[300px] xl:mt-8 text-center align-middle"
                        />
                    </div>
                </div>
            </section>
            <section>
                <SectionHeader
                    className="border-b border-t"
                    title="news"
                    description="Breaking headlines across our core coverage categories."
                    linkTitle={
                        <span className="flex align-middle">
                            more from news
                            <svg
                                width={8}
                                height={4}
                                viewBox="0 0 8 4"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="ml-1 mt-1.5"
                                preserveAspectRatio="none"
                            >
                                <path d="M6.005 1.5H0V2.5H6.005V4L8 2L6.005 0V1.5Z" fill="#5637CD" />
                            </svg>
                        </span>
                    }
                    linkUrl="/news"
                />
                {readMoreArticles && (
                    <NewsSection articles={readMoreArticles} categoryTabs={categories.filter(c => c.count > 0)} />
                )}
            </section>
        </main>
    );
};

export default NewsArticlePage;
